import { useEffect, useState } from "react";
import { api, inr } from "../../lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, Package, Sparkles, ArrowUpRight, AlertTriangle, Banknote, Save } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const COLORS = ["#D4AF37", "#8B7355", "#3B2F2F", "#2E4A35", "#993322", "#78716C"];

export default function Dashboard() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [codFee, setCodFee] = useState("");
  const [codSaved, setCodSaved] = useState(null);
  const [savingCod, setSavingCod] = useState(false);

  const loadSettings = () => {
    api.get("/admin/settings").then(r => {
      setCodFee(String(r.data.cod_fee ?? 9));
      setCodSaved(r.data.cod_fee);
    }).catch(()=>{});
  };

  const saveCodFee = async () => {
    const v = parseFloat(codFee);
    if (isNaN(v) || v < 0 || v > 500) { toast.error("Enter a value between 0 and 500"); return; }
    setSavingCod(true);
    try {
      await api.post("/admin/settings/cod-fee", { cod_fee: v });
      setCodSaved(v);
      toast.success(`COD handling fee updated to ₹${v}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSavingCod(false); }
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/analytics/summary?range=${range}`),
      api.get(`/admin/analytics/insights`),
    ]).then(([s, i]) => { setData(s.data); setInsights(i.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
    loadSettings();
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range]);

  const seed = async () => {
    try { await api.post("/seed"); toast.success("Sample data loaded"); load(); }
    catch { toast.error("Seed failed"); }
  };

  if (loading || !data) return <div className="text-stone-500">Loading dashboard…</div>;

  const emptyStore = data.orders === 0 && data.total_customers === 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-stone-900">Dashboard</h1>
          <div className="text-sm text-stone-500 mt-1">Business at a glance · {range}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={seed} data-testid="seed-btn" className="text-sm border border-stone-200 rounded-md px-3 py-2 bg-white hover:bg-stone-50">Load sample data</button>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-40 bg-white" data-testid="range-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="365d">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {emptyStore && (
        <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-lg p-4 text-sm text-stone-700">
          <strong>Welcome!</strong> No sales data yet. Click <em>Load sample data</em> to populate the dashboard with a realistic demo.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={DollarSign} label="Revenue" value={inr(data.revenue)} sub={`${inr(data.today_revenue)} today`} testid="kpi-revenue"/>
        <KPI icon={ShoppingCart} label="Orders" value={data.orders} sub={`AOV ${inr(data.aov)}`} testid="kpi-orders"/>
        <KPI icon={Users} label="Customers" value={data.customers} sub={`${data.total_customers} lifetime`} testid="kpi-customers"/>
        <KPI icon={TrendingUp} label="Profit" value={inr(data.profit)} sub={`${data.revenue>0?Math.round(data.profit/data.revenue*100):0}% margin`} testid="kpi-profit"/>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-white border border-stone-200 rounded-lg p-6">
          <h3 className="font-outfit font-semibold mb-4">Revenue trend</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="date" stroke="#78716C" fontSize={11} />
              <YAxis stroke="#78716C" fontSize={11} />
              <Tooltip contentStyle={{fontSize:12}} />
              <Line type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2.5} dot={{r:2}} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-stone-200 rounded-lg p-6">
          <h3 className="font-outfit font-semibold mb-4">Revenue by category</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.by_category} dataKey="revenue" nameKey="category" innerRadius={50} outerRadius={90}>
                {data.by_category.map((_, i) => <Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
              </Pie>
              <Legend wrapperStyle={{fontSize:11}}/>
              <Tooltip contentStyle={{fontSize:12}}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-white border border-stone-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-semibold">Top products</h3>
            <Link to="/admin/products" className="text-xs text-[#8B7355] flex items-center gap-1">View all <ArrowUpRight className="w-3 h-3"/></Link>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.top_products} layout="vertical" margin={{left:20}}>
              <XAxis type="number" stroke="#78716C" fontSize={11}/>
              <YAxis dataKey="name" type="category" width={140} fontSize={11} stroke="#78716C"/>
              <Tooltip contentStyle={{fontSize:12}}/>
              <Bar dataKey="revenue" fill="#3B2F2F" radius={[0,4,4,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-stone-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[#D4AF37]"/>
            <h3 className="font-outfit font-semibold">Actionable insights</h3>
          </div>
          <div className="space-y-3 max-h-80 overflow-auto">
            {insights.slice(0,8).map((it, i) => (
              <div key={i} data-testid={`insight-${i}`}
                   className={`p-3 rounded-md border text-sm ${
                     it.severity==="positive"?"bg-[#2E4A35]/5 border-[#2E4A35]/30":
                     it.severity==="warning"?"bg-[#993322]/5 border-[#993322]/30":
                     "bg-stone-50 border-stone-200"}`}>
                <div className="flex items-start gap-2">
                  {it.severity==="warning" ? <AlertTriangle className="w-4 h-4 text-[#993322] mt-0.5 shrink-0"/> :
                   it.severity==="positive" ? <TrendingUp className="w-4 h-4 text-[#2E4A35] mt-0.5 shrink-0"/> :
                   <Sparkles className="w-4 h-4 text-[#8B7355] mt-0.5 shrink-0"/>}
                  <div>
                    <div className="font-medium text-stone-800">{it.title}</div>
                    <div className="text-xs text-stone-600 mt-1">{it.action}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Store settings — quick edit card */}
      <div className="bg-white border border-stone-200 rounded-lg p-6" data-testid="store-settings-card">
        <div className="flex items-center gap-2 mb-4">
          <Banknote className="w-5 h-5 text-[#D4AF37]"/>
          <h3 className="font-outfit text-lg font-bold text-stone-900">Store settings</h3>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">COD handling fee (₹)</label>
            <div className="flex gap-2 mt-2">
              <Input type="number" min="0" max="500" step="1"
                     value={codFee} onChange={(e)=>setCodFee(e.target.value)}
                     onKeyDown={(e)=>{ if(e.key==="Enter") saveCodFee(); }}
                     data-testid="cod-fee-input"
                     className="max-w-40"/>
              <button onClick={saveCodFee} disabled={savingCod || parseFloat(codFee) === codSaved}
                      data-testid="cod-fee-save"
                      className="bg-[#3B2F2F] text-white rounded-md px-4 text-sm font-medium hover:bg-black disabled:opacity-40 flex items-center gap-1.5">
                <Save className="w-3.5 h-3.5"/> {savingCod ? "Saving…" : "Save"}
              </button>
            </div>
            <div className="text-xs text-stone-500 mt-2">
              Applied automatically to every new Cash-on-Delivery order.
              Current live value: <span className="font-outfit font-semibold text-[#3B2F2F]">₹{codSaved ?? "…"}</span>.
              Set to <code className="bg-stone-100 px-1 rounded">0</code> to waive the fee entirely.
            </div>
          </div>
          <div className="text-xs text-stone-500 border-l border-stone-200 pl-6">
            <div className="uppercase tracking-widest text-[10px] font-semibold mb-1">Why charge a COD fee?</div>
            Covers courier collection charges + returned-shipment risk (typical India RTO rate: 15-25% on COD).
            <br/><br/>
            When online payments go live, you can drop this to ₹0 to nudge customers toward prepaid orders.
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-stone-500">{label}</div>
        <Icon className="w-4 h-4 text-[#D4AF37]"/>
      </div>
      <div className="mt-3 font-outfit text-3xl font-bold text-stone-900">{value}</div>
      <div className="text-xs text-stone-500 mt-1">{sub}</div>
    </div>
  );
}
